// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SalesPersonCard, { SalesPersonCardStyles } from "@werner/App/Components/MiniMyAccount/SalesPersonCard";
import { getSalesPersonByType } from "@werner/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import { XMappedLink } from "@werner/Overrides/Widgets/Common/HeaderSignIn";
import { mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC, useEffect } from "react";

interface MiniSignInProps {
    myAccountLinks?: {
        title: string;
        links: XMappedLink[];
    };
    isPunchOutSession?: boolean;
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

interface InfoSectionProps {
    title?: string;
    label?: string;
    phoneNumber?: string;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    header?: InjectableCss;
    titleLink?: LinkPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoTitle?: TypographyPresentationProps;
    linksContainer?: InjectableCss;
    link?: LinkPresentationProps;
    sectionWrapper?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    customerInfoSecondaryText?: TypographyPresentationProps;
    salesPersonSection?: SalesPersonCardStyles;
    actionWrapper?: InjectableCss;
    changeShipToButton?: ButtonPresentationProps;
    signOutLink?: LinkPresentationProps;
}
// XNG-Changes
export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({
    isPunchOutSession,
    myAccountLinks,
    displayChangeCustomerLink,
    onSignOutHandler,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const dispatch = useAppDispatch();
    const history = useHistory();

    const {
        currentBillTo,
        billToId,
        currentShipTo,
        myAccountPageUrl,
        changeCustomerPageUrl,
        insideSalesPerson,
        outsideSalesPerson,
        enableRockwellConfigurator,
        rockwellConfiguratorPageUrl,
    } = useAppSelector(state => ({
        billToId: state.context.session.billToId,
        currentBillTo: getCurrentBillToState(state).value,
        currentShipTo: getCurrentShipToState(state).value,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        insideSalesPerson: getSalesPersonByType(state, "inside"),
        outsideSalesPerson: getSalesPersonByType(state, "outside"),
        changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        enableRockwellConfigurator:
            getSettingsCollection(state).rockwellConfiguratorSettings.enableRockwellConfigurator,
        rockwellConfiguratorPageUrl: getPageLinkByPageType(state, "RockwellConfiguratorPage")?.url,
    }));

    useEffect(() => {
        if (billToId && !currentBillTo) {
            dispatch(loadCurrentBillTo());
        }
    }, []);
    return (
        <StyledWrapper {...styles.container} data-test-selector="miniMyMyAccount">
            <StyledWrapper {...styles.header}>
                <Link {...styles.titleLink} href={myAccountPageUrl} aria-label="My Account">
                    {translate("My Account")}
                </Link>
            </StyledWrapper>
            <GridContainer {...styles.infoGridContainer}>
                <GridItem {...styles.infoGridItem}>
                    {/* // XNG-Changes */}
                    <Typography {...styles.infoTitle}>{myAccountLinks?.title}</Typography>
                    <StyledWrapper {...styles.linksContainer}>
                        {myAccountLinks?.links?.map((link, index) => {
                            if (link.url === rockwellConfiguratorPageUrl && !enableRockwellConfigurator) {
                                return null;
                            }
                            return (
                                <Link
                                    href={link?.url}
                                    {...styles.link}
                                    key={`${link.title}_${link.url}_${index}`}
                                    aria-label={`${link?.title}`}
                                >
                                    {link?.title}
                                </Link>
                            );
                        })}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <Typography {...styles.infoTitle}>{translate("Profile Information")}</Typography>
                    <StyledWrapper {...styles.sectionWrapper}>
                        <Typography {...styles.sectionTitle}>{translate("Customer")}</Typography>
                        {currentBillTo?.companyName && (
                            <Typography {...styles.customerInfoSecondaryText}>{currentBillTo.companyName}</Typography>
                        )}
                        {currentBillTo?.customerNumber && (
                            <Typography {...styles.customerInfoSecondaryText}>
                                {translate("Bill To")}: #{currentBillTo?.customerNumber}
                            </Typography>
                        )}
                        {currentShipTo?.customerSequence && (
                            <Typography {...styles.customerInfoSecondaryText}>
                                {translate("Ship To")}: #{currentShipTo.customerSequence}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <SalesPersonCard salesPersonType="inside" extendedStyles={styles.salesPersonSection} />
                    <SalesPersonCard salesPersonType="outside" extendedStyles={styles.salesPersonSection} />
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.actionWrapper}>
                {displayChangeCustomerLink && (
                    <Button
                        {...styles.changeShipToButton}
                        aria-label="changeCustomer"
                        onClick={() => {
                            changeCustomerPageUrl && history.push(changeCustomerPageUrl);
                        }}
                    >
                        {translate("Change Ship-To")}
                    </Button>
                )}
                <Link
                    {...styles.signOutLink}
                    onClick={() => {
                        onSignOutHandler?.();
                    }}
                    aria-label="signOut"
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out of Account")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
